package top.wcpe.allincore.ui.runtime.render.layer

import top.wcpe.allincore.ui.runtime.internal.log.UiLog
import top.wcpe.allincore.ui.runtime.render.StateStacks
import top.wcpe.allincore.ui.runtime.render.UiRenderBridge
import top.wcpe.allincore.ui.runtime.render.model.ImageStyle
import top.wcpe.allincore.ui.runtime.render.model.RenderBatch
import top.wcpe.allincore.ui.runtime.render.model.RenderCommand
import top.wcpe.allincore.ui.runtime.render.model.RenderCommandType
import top.wcpe.allincore.ui.runtime.render.model.RenderLayer
import top.wcpe.allincore.ui.runtime.render.model.UiRenderZ
import top.wcpe.allincore.ui.runtime.theme.FontAccess

/**
 * 层缓冲桥接：实现 [UiRenderBridge]，将所有绘制命令按层分组缓冲，
 * 在 [endFrame] 时按 0→5 顺序逐层回放到真正的 delegate 桥。
 */
@Suppress("TooManyFunctions", "LargeClass", "MagicNumber")
class LayerBufferingBridge(
    private val delegate: UiRenderBridge,
    private val traceCapture: ((DrawTraceEntry) -> Unit)?,
) : UiRenderBridge, UnsupportedCapabilityCapture {

    constructor(delegate: UiRenderBridge) : this(delegate, null)

    /** 不带居中偏移的屏幕坐标系 delegate，用于回放 screen-space 浮层（fullscreen/centered modal 等）。 */
    private val screenDelegate: UiRenderBridge = delegate.screenSpaceBridge()

    /** 6 层缓冲区，每层一个 [DrawRecord] 列表。 */
    private val layerBuffers: Array<MutableList<DrawRecord>> =
        Array(RenderLayer.COUNT) { mutableListOf() }

    /**
     * 屏幕坐标系浮层缓冲：fullscreen/centered/anchor modal 走 [beginScreenSpace]/[endScreenSpace]
     * 录制到此，在 6 层全部回放之后再经 [screenDelegate] 回放，
     * 保证以 painter 顺序盖在所有内容之上（修复 modal 被缓冲层盖到背景后方的问题）。
     */
    private val screenSpaceBuffer = mutableListOf<DrawRecord>()

    /** 当前是否处于 screen-space 录制模式：true 时绘制记录进 [screenSpaceBuffer]。 */
    private var screenSpaceMode = false

    /**
     * 屏幕坐标系 tooltip 缓冲：screen-space modal（centered/fullscreen/anchor）子树内的 DSL tooltip
     * 走 [beginScreenSpaceTooltip]/[endScreenSpaceTooltip] 录入此处，在 [screenSpaceBuffer]（modal 本体，
     * 含其内物品图标）之后、以 [UiRenderZ.TOOLTIP] 档回放。
     *
     * 不能与 modal 共用 [screenSpaceBuffer]：该缓冲整段以单一 [UiRenderZ.MODAL] 档回放，
     * 而 modal 内 vanilla 物品图标会在 pose z 之上再叠 +150（实际 1650），高于 tooltip 自身的 +0（1500），
     * 深度测试下物品反盖 tooltip。独立缓冲抬到 TOOLTIP(2500) 档即稳压 modal 内物品 / 数量角标。
     */
    private val screenSpaceTooltipBuffer = mutableListOf<DrawRecord>()

    /** 当前是否处于 screen-space tooltip 录制模式：true 时绘制记录进 [screenSpaceTooltipBuffer]。 */
    private var screenSpaceTooltipMode = false

    /**
     * filter 组（CSS filter: blur）捕获栈：每进入一个 [beginFilterGroup] 压入一个独立的子命令缓冲，
     * begin..end 之间的 [addRecord] 改路由进栈顶缓冲（含 StateSnapshot），
     * [endFilterGroup] 时把栈顶缓冲封装为一条 [RenderCommandType.FILTER_GROUP] 记录
     * 放回外层目标（外层 filter 组的捕获缓冲或当前层缓冲）。
     *
     * 用栈而非单缓冲是为支持嵌套 blur（外层 blur 内部还有 blur 的元素）：内层 end 时其 FILTER_GROUP
     * 记录落入外层捕获缓冲，外层 end 时整体再封装。平台层若嵌套超出 FBO 池容量会在回放期降级直通。
     */
    private val filterGroupStack = ArrayDeque<FilterGroupCapture>()

    /**
     * 混合组（#8 mix-blend-mode）捕获栈：与 [filterGroupStack] 同构，[pushBlendMode]
     * 压入、[popBlendMode] 弹出并封装为一条 [RenderCommandType.BLEND_GROUP] 记录。
     *
     * 与 filter 组共用 [captureDepthCounter] 标记入栈深度：[addRecord] 在两栈都活跃时
     * 按深度选最内层捕获，正确处理 blur 与 blend 在同一元素上交叠的场景（UIRenderer 中 blur begin 先于
     * blend push，但 blur end 先于 blend pop——封装在 end/pop 时刻发生，最内层栈顶深度最大者承接子命令）。
     */
    private val blendGroupStack = ArrayDeque<BlendGroupCapture>()


    /**
     * 历史组（FR-UI-023 historyLayer）捕获栈：与 [filterGroupStack] 同构。
     */
    private val historyGroupStack = ArrayDeque<HistoryGroupCapture>()

    /**
     * 捕获入栈深度计数器：每次 [beginFilterGroup] 或 [pushBlendMode] 自增并赋给新捕获，
     * 供 [addRecord] 在 filter 组与 blend 组同时活跃时判定哪个是最内层（深度最大者）。
     */
    private var captureDepthCounter = 0

    /**
     * 当前是否处于浮层阶段：由 [beginFloatingPhase] 置位。置位后 [beginLayer]
     * 不再切层，浮层段内所有绘制（dropdown / modal / menu / toast / tooltip 及其内容子树）
     * 统一停留在 [RenderLayer.FLOATING]，按录制顺序整体抬 z 回放，盖住 ITEM 层物品图标。
     *
     * 修复 issue #48：tooltip/菜单内容子树的首个元素（默认层 BACKGROUND）会把当前层切回底层，
     * 击穿外层设置的 FLOATING，导致浮层被随后回放的物品图标（z≈150）穿透。
     */
    private var floatingPhase = false

    /** 当前活动层。 */
    private var currentLayer: RenderLayer = RenderLayer.BACKGROUND

    /** 全局录制序号，单调递增。 */
    private var sequence = 0

    /** 实际回放序号，单帧从零开始且严格递增。 */
    private var traceSequence = 0

    /** 当前回放阶段，仅在 [endFrame] 回放期间有效。 */
    private var tracePhase: DrawTracePhase = DrawTracePhase.BACKGROUND

    /** 当前常规层号；屏幕阶段固定为空。 */
    private var traceLayerId: Int? = RenderLayer.BACKGROUND.id

    /** 正常 UIRenderer 路径可选的 trace/诊断观测端。 */
    private val traceObserver = delegate as? DrawTraceObserver

    /** 仅在 trace 采集开启时维护当前元素路径，普通平台桥不承担该状态。 */
    private var currentNodePath: String? = null

    /** 元素渲染作用域栈；能力只允许绑定当前作用域内记录，禁止泄漏到兄弟元素。 */
    private val unsupportedContextStack = ArrayDeque<UnsupportedContextScope>()

    /** 每条真实绘制记录对应的降级能力，保证诊断携带实际 phase/sequence。 */
    private val unsupportedCapabilitiesByRecord = mutableMapOf<DrawRecord, MutableSet<UnsupportedRenderCapability>>()

    // === 内部状态栈（拆分为可单测的中立 StateStacks，ADR-0072 决策 3） ===
    private val stateStacks = StateStacks()
    private val layerStack = ArrayDeque<RenderLayer>()

    // ===== 帧生命周期 =====

    /** 开始新帧：清空所有层缓冲和内部状态栈。 */
    fun beginFrame() {
        layerBuffers.forEach { it.clear() }
        screenSpaceBuffer.clear()
        screenSpaceMode = false
        screenSpaceTooltipBuffer.clear()
        screenSpaceTooltipMode = false
        floatingPhase = false
        filterGroupStack.clear()
        blendGroupStack.clear()
        historyGroupStack.clear()
        captureDepthCounter = 0
        stateStacks.clear()
        layerStack.clear()
        sequence = 0
        traceSequence = 0
        tracePhase = DrawTracePhase.BACKGROUND
        traceLayerId = RenderLayer.BACKGROUND.id
        currentLayer = RenderLayer.BACKGROUND
        currentNodePath = null
        unsupportedContextStack.clear()
        unsupportedCapabilitiesByRecord.clear()
    }

    internal fun capturesRuntimeNodePath(): Boolean = traceCapture != null || traceObserver != null

    internal fun currentRuntimeNodePath(): String? = currentNodePath

    internal fun updateRuntimeNodePath(nodePath: String?) {
        if (capturesRuntimeNodePath()) currentNodePath = nodePath
    }

    override fun beginUnsupportedContext() {
        unsupportedContextStack.addFirst(
            UnsupportedContextScope(currentRecordingPhase(), currentRecordingLayerId()),
        )
    }

    override fun captureUnsupportedCapability(capability: UnsupportedRenderCapability) {
        val scope = unsupportedContextStack.firstOrNull()
        if (scope == null) {
            traceObserver?.onUnsupportedCapability(
                capability,
                UnsupportedRenderContext(currentRecordingPhase(), currentRecordingLayerId(), -1),
            )
        } else {
            scope.capabilities.add(capability)
        }
    }

    override fun endUnsupportedContext() {
        val scope = unsupportedContextStack.removeFirstOrNull() ?: return
        scope.capabilities.forEach { capability ->
            traceObserver?.onUnsupportedCapability(
                capability,
                UnsupportedRenderContext(scope.phase, scope.layerId, -1),
            )
        }
    }

    /**
     * 设置当前活动层。浮层阶段（[beginFloatingPhase] 后）忽略切层，
     * 使浮层内容子树无法把当前层切回底层，统一停留在 [RenderLayer.FLOATING]。
     */
    fun beginLayer(layer: RenderLayer) {
        if (!floatingPhase) {
            currentLayer = layer
        }
    }

    /**
     * 进入浮层阶段：把当前层固定为 [RenderLayer.FLOATING] 并锁定，后续 [beginLayer]
     * 调用一律忽略。供 UIRenderer 在主树 DFS 之后、
     * 绘制 dropdown / modal / menu / toast / tooltip 之前调用，保证整段浮层内容（含其内容子树里
     * 默认 BACKGROUND 层的 Box/Text）都落在 FLOATING 层，回放时整体抬 z 盖住物品图标（issue #48）。
     *
     * 不影响 screen-space 路径：[addRecord] 先判 [screenSpaceMode]，
     * fullscreen/centered/anchor modal 仍走屏幕坐标缓冲在所有层之后回放。
     */
    fun beginFloatingPhase() {
        floatingPhase = true
        currentLayer = RenderLayer.FLOATING
    }

    /**
     * 进入 screen-space 录制模式：后续绘制记录改存入 [screenSpaceBuffer]，
     * 在 endFrame 所有层回放之后经 [screenDelegate]（无居中偏移）回放，盖在最上层。
     * 供 fullscreen/centered/anchor modal 使用，必须与 [endScreenSpace] 成对调用。
     */
    fun beginScreenSpace() {
        screenSpaceMode = true
    }

    /** 退出 screen-space 录制模式，后续绘制回到当前层缓冲。 */
    fun endScreenSpace() {
        screenSpaceMode = false
    }

    /**
     * 进入 screen-space tooltip 录制模式：后续绘制记录改存入 [screenSpaceTooltipBuffer]，
     * 在 endFrame 的 [screenSpaceBuffer]（modal 本体）之后、经 [screenDelegate] 以
     * [UiRenderZ.TOOLTIP] 档回放，稳压 modal 内物品图标（+150）。供 screen-space modal 子树内
     * 的 DSL tooltip 使用，必须与 [endScreenSpaceTooltip] 成对调用。
     */
    fun beginScreenSpaceTooltip() {
        screenSpaceTooltipMode = true
    }

    /** 退出 screen-space tooltip 录制模式，后续绘制回到当前层缓冲。 */
    fun endScreenSpaceTooltip() {
        screenSpaceTooltipMode = false
    }

    /**
     * 录一条「抬高浮层」屏障：回放到该位置时先把已录入的延迟顶点提交上屏，再把 z 抬高 [z]，
     * 使其后回放的绘制盖住同一缓冲区里先画的内容。必须与 [recordOverlayRestore] 成对。
     *
     * 两件事缺一不可：
     * - **flush**：平台层按 RenderType 分组提交同一批延迟顶点，文本组恒排在 GUI 矩形组之后，
     *   同一缓冲区内「先画的文字」会浮到「后画的矩形」之上。层与层之间由 [replayBuffer] 末尾的
     *   flush 天然隔开，缓冲区内部则要靠本屏障。
     * - **抬 z**：带阴影的文字（`fontShadow`）正文会被 vanilla 额外 `z + 0.03`（用来盖住自己的
     *   阴影），而 `RenderType.gui()` 是 LEQUAL 深度测试——同档位下后画的浮层矩形深度更大会被
     *   拒绝，于是只有这类文字漏在浮层之上。抬一档 z 才能真正盖住。
     *
     * 屏障本身不绘制任何东西，一次浮层绘制只录一对，开销可忽略。
     */
    fun recordOverlayLift(z: Float) {
        addRecord(DrawRecord.builder().flushBarrier(true).barrierPushZ(z).sequence(sequence++).build())
    }

    /** 录一条还原屏障：提交浮层顶点后把 [recordOverlayLift] 抬起的 z 还原。 */
    fun recordOverlayRestore() {
        addRecord(DrawRecord.builder().flushBarrier(true).barrierPopZ(true).sequence(sequence++).build())
    }

    /**
     * 按当前模式把绘制记录路由到目标缓冲。
     *
     * 优先级：filter 组 / blend 组捕获中 → 最内层（入栈深度最大）捕获缓冲；
     * 否则 screen-space tooltip 模式 → [screenSpaceTooltipBuffer]（抬到更高 z 档，避免被 modal 内物品图标反盖）；
     * 否则 screen-space 模式 → [screenSpaceBuffer]；否则 → 当前层缓冲。
     * 捕获优先于 screen-space：blur/blend 元素在浮层 modal 内时，子命令仍应先进捕获缓冲。
     */
    private fun addRecord(record: DrawRecord) {
        currentNodePath?.let(record::assignNodePathIfAbsent)
        associateUnsupportedCapabilities(record)
        val capture = innermostCaptureBuffer()
        when {
            capture != null -> capture.add(record)
            // tooltip 优先于 screen-space modal：抬到独立的更高 z 档，避免被 modal 内物品图标(+150)反盖
            screenSpaceTooltipMode -> screenSpaceTooltipBuffer.add(record)
            screenSpaceMode -> screenSpaceBuffer.add(record)
            else -> layerBuffers[currentLayer.id].add(record)
        }
    }

    private fun associateUnsupportedCapabilities(record: DrawRecord) {
        unsupportedContextStack.forEach { scope ->
            if (scope.capabilities.isEmpty()) return@forEach
            unsupportedCapabilitiesByRecord.getOrPut(record) { linkedSetOf() }.addAll(scope.capabilities)
            scope.capabilities.clear()
        }
    }

    private fun currentRecordingPhase(): DrawTracePhase = when {
        screenSpaceTooltipMode -> DrawTracePhase.SCREEN_TOOLTIP
        screenSpaceMode -> DrawTracePhase.SCREEN_MODAL
        else -> DrawTracePhase.entries[currentLayer.id]
    }

    private fun currentRecordingLayerId(): Int? =
        if (screenSpaceTooltipMode || screenSpaceMode) null else currentLayer.id

    /**
     * 取最内层（入栈深度最大）的捕获缓冲：在 filter 组与 blend 组两栈顶中选 depth 更大者。
     * 两栈都空时返回 `null`（无捕获，走 screen-space / 层缓冲）。
     */
    private fun innermostCaptureBuffer(): MutableList<DrawRecord>? {
        val fg = filterGroupStack.firstOrNull()
        val bg = blendGroupStack.firstOrNull()
        val hg = historyGroupStack.firstOrNull()
        // 取 depth 最大的活动捕获缓冲（filter / blend / history 三栈）
        val tops = listOfNotNull(
            fg?.let { it.depth to it.commands },
            bg?.let { it.depth to it.commands },
            hg?.let { it.depth to it.commands },
        )
        if (tops.isEmpty()) return null
        return tops.maxBy { it.first }.second
    }

    /**
     * 结束当前帧：按 0→5 顺序逐层回放每条 DrawRecord 到 delegate。
     *
     * 每条记录先推入其快照状态，回放绘制调用，再弹出状态，保证 delegate 状态栈干净。
     */
    fun endFrame() {
        for (layerId in 0 until RenderLayer.COUNT) {
            // 根因 B（物品遮挡浮层）：OVERLAY/FLOATING 层整体抬高 z，盖住 ITEM 层 vanilla 物品
            // （renderItem 内部固定 z≈150）。靠 GUI 的 LEQUAL 深度排序，与 HudRenderHook 既有
            // pushZ(200) 模式一致；不清深度缓冲，避免破坏 HUD/世界等其它渲染 pass。
            replayBuffer(
                layerBuffers[layerId],
                delegate,
                floatingLayerZ(layerId),
                DrawTracePhase.entries[layerId],
                layerId,
            )
        }
        // 屏幕坐标系浮层（fullscreen/centered/anchor modal 等）：在 6 层全部回放之后再回放，
        // 以 painter 顺序盖在所有内容之上（修复 modal 被缓冲层盖到背景后方）。
        // 经 screenDelegate 回放（无居中偏移），抬到 MODAL 档（高于 FLOATING 下拉框）。
        replayBuffer(screenSpaceBuffer, screenDelegate, UiRenderZ.MODAL, DrawTracePhase.SCREEN_MODAL, null)
        // 屏幕坐标系 modal 子树内的 DSL tooltip：在 modal 本体之后、抬到 TOOLTIP 档回放，
        // 稳压 modal 内 vanilla 物品图标（MODAL+150=1650）与数量角标（MODAL+200=1700）。
        replayBuffer(
            screenSpaceTooltipBuffer,
            screenDelegate,
            UiRenderZ.TOOLTIP,
            DrawTracePhase.SCREEN_TOOLTIP,
            null,
        )
    }

    /**
     * 把一个缓冲区按记录顺序回放到 target，可选整体抬高 z（0 表示不抬高）。
     * 每条记录先推入快照状态、回放、再弹出，保证 target 状态栈干净。
     */
    private fun replayBuffer(
        buffer: List<DrawRecord>,
        target: UiRenderBridge,
        z: Float,
        phase: DrawTracePhase,
        layerId: Int?,
    ) {
        if (buffer.isEmpty()) {
            return
        }
        tracePhase = phase
        traceLayerId = layerId
        val lift = z != 0f
        if (lift) {
            target.pushZ(z)
        }
        for (record in buffer) {
            // 根因 A（立即/延迟顺序）：立即模式特效（Tesselator 直接 draw）回放前，
            // 先 flush 已录入的延迟 2D 内容（GuiGraphics bufferSource），
            // 保证延迟内容先落盘、特效后画在其之上，而非被层末尾 flush 的延迟内容反盖。
            replayRecordWithState(target, record)
        }
        target.flush()
        if (lift) {
            target.popZ()
        }
    }

    /**
     * 回放单条记录：立即模式特效回放前先 flush 延迟内容，再推入快照状态、回放绘制、弹出状态。
     * [replayBuffer]/[replayFilterGroup]/[replayBlendGroup] 共用，消除三处重复回放循环。
     */
    private fun replayRecordWithState(target: UiRenderBridge, record: DrawRecord) {
        // 屏障只提交先前批次并按需抬高 / 还原 z，不绘制任何内容（见 [recordOverlayLift]）。
        if (record.flushBarrier()) {
            target.flush()
            val lift = record.barrierPushZ()
            if (lift != 0f) {
                target.pushZ(lift)
            }
            if (record.barrierPopZ()) {
                target.popZ()
            }
            return
        }
        val entry = DrawTraceEntry(tracePhase, traceLayerId, traceSequence++, record)
        traceCapture?.invoke(entry)
        traceObserver?.onDrawTrace(entry)
        unsupportedCapabilitiesByRecord[record]?.forEach { capability ->
            traceObserver?.onUnsupportedCapability(capability, entry)
        }
        if (isImmediateRecord(record.type())) {
            target.flush()
        }
        // record.state() 录制路径恒非空；null 为退化路径，回退 EMPTY 走空栈无操作（原 Java 此路径会 NPE，改安全降级）。
        val state = record.state() ?: StateSnapshot.EMPTY
        pushStateToDelegate(target, state)
        replayDrawCall(target, record)
        popStateFromDelegate(target, state)
    }

    /**
     * 保存当前层到层栈。
     */
    fun pushLayer() {
        layerStack.addFirst(currentLayer)
    }

    /**
     * 从层栈恢复当前层。
     */
    fun popLayer() {
        layerStack.removeFirstOrNull()?.let { currentLayer = it }
    }

    // ===== 状态快照 =====

    /**
     * 根据当前内部状态栈创建不可变快照（委托 [StateStacks.snapshot]）。
     */
    fun snapshotState(): StateSnapshot = stateStacks.snapshot()

    // ===== 内部状态操作（录制期状态一律委托 StateStacks，语义见其 KDoc） =====

    override fun pushClip(x: Float, y: Float, width: Float, height: Float) {
        stateStacks.pushClip(x, y, width, height)
    }

    override fun popClip() {
        stateStacks.popClip()
    }

    override fun pushAlpha(alpha: Float) {
        stateStacks.pushAlpha(alpha)
    }

    override fun popAlpha() {
        stateStacks.popAlpha()
    }

    override fun pushColorMatrix(matrix4x5: FloatArray) {
        stateStacks.pushColorMatrix(matrix4x5)
    }

    override fun popColorMatrix() {
        stateStacks.popColorMatrix()
    }

    override fun supportsFilterGroup(): Boolean = captureUnsupportedWhenNeeded(
        UnsupportedRenderCapability.FILTER_GROUP,
        delegate.supportsFilterGroup(),
    )

    /**
     * 开启 filter 组捕获：压入一个独立子命令缓冲，记录矩形与模糊半径。
     * begin..end 之间的所有绘制经 [addRecord] 改进该缓冲，不落主层/不落屏幕浮层。
     */
    override fun beginFilterGroup(blurRadius: Float, x: Float, y: Float, w: Float, h: Float) {
        filterGroupStack.addFirst(FilterGroupCapture(blurRadius, x, y, w, h, ++captureDepthCounter))
    }

    /**
     * 结束 filter 组捕获：把栈顶捕获缓冲封装为一条 [RenderCommandType.FILTER_GROUP] 记录，
     * 经 [addRecord] 放回外层目标（外层捕获缓冲或当前层缓冲）。
     *
     * 记录自身的 [DrawRecord.state] 用「封装时刻」的状态快照（含外层 transform/clip），
     * 使回放时离屏合成 quad 落在正确的屏幕变换下；子命令各自携带录制时的状态快照。
     */
    override fun endFilterGroup() {
        val capture = filterGroupStack.removeFirstOrNull()
        if (capture == null) {
            log.warn("endFilterGroup 调用时 filter 组捕获栈为空，忽略")
            return
        }
        // 捕获为空（filter 元素无任何可见绘制）时仍记录一条空 FILTER_GROUP，回放走 begin→（无子命令）→end，
        // 平台层 acquire→clear→两 pass→合成空内容，零像素无副作用；避免静默吞掉。
        addRecord(
            DrawRecord.builder()
                .type(RenderCommandType.FILTER_GROUP)
                .pos(capture.x, capture.y, capture.w, capture.h)
                .blurRadius(capture.blurRadius)
                .filterGroupCommands(capture.commands)
                .sequence(sequence++)
                .state(snapshotState())
                .build(),
        )
    }

    /**
     * 开启 history 组捕获（FR-UI-023 historyLayer）：压入独立子命令缓冲，记录矩形、elementId 与历史层参数。
     * begin..end 之间的所有绘制经 [addRecord] 改进该缓冲，不落主层/不落屏幕浮层。与 [beginFilterGroup] 同构，
     * 区别在于额外携带 elementId 与 specFields 两个负载。
     */
    override fun beginHistoryCapture(elementId: String, spec: Map<String, *>, x: Float, y: Float, w: Float, h: Float) {
        historyGroupStack.addFirst(
            HistoryGroupCapture(
                elementId,
                spec,
                CaptureBounds(x, y, w, h),
                ++captureDepthCounter,
            ),
        )
    }

    /**
     * 结束 history 组捕获：把栈顶捕获缓冲封装为一条 [RenderCommandType.HISTORY_GROUP] 记录，
     * 经 [addRecord] 放回外层目标（外层捕获缓冲或当前层缓冲）。记录自身 [DrawRecord.state] 用「封装时刻」
     * 的状态快照（含外层 transform/clip），使回放时离屏合成落在正确屏幕变换下；子命令各自携带录制时快照。
     */
    override fun endHistoryCapture() {
        val capture = historyGroupStack.removeFirstOrNull()
        if (capture == null) {
            log.warn("endHistoryCapture 调用时 history 组捕获栈为空，忽略")
            return
        }
        addRecord(
            DrawRecord.builder()
                .type(RenderCommandType.HISTORY_GROUP)
                .pos(capture.bounds.x, capture.bounds.y, capture.bounds.w, capture.bounds.h)
                .historyElementId(capture.elementId)
                .historySpec(capture.spec)
                .historyGroupCommands(capture.commands)
                .sequence(sequence++)
                .state(snapshotState())
                .build(),
        )
    }


    override fun supportsBackdropFilter(): Boolean = captureUnsupportedWhenNeeded(
        UnsupportedRenderCapability.BACKDROP_FILTER,
        delegate.supportsBackdropFilter(),
    )

    override fun supportsHistoryCapture(): Boolean = captureUnsupportedWhenNeeded(
        UnsupportedRenderCapability.HISTORY_CAPTURE,
        delegate.supportsHistoryCapture(),
    )

    /**
     * 录一条 [RenderCommandType.BACKDROP_FILTER] 记录（背景模糊，#8）。
     *
     * backdrop 不是 begin/end 捕获区间，是单点立即操作：把矩形与半径录进当前缓冲，
     * 回放时（[replayBackdrop]）先 flush 保证更早层落主帧缓冲，再就地采样背景做高斯。
     */
    override fun applyBackdropBlur(blurRadius: Float, x: Float, y: Float, w: Float, h: Float) {
        addRecord(
            DrawRecord.builder()
                .type(RenderCommandType.BACKDROP_FILTER)
                .pos(x, y, w, h)
                .backdropBlurRadius(blurRadius)
                .sequence(sequence++)
                .state(snapshotState())
                .build(),
        )
    }

    override fun supportsBlendMode(): Boolean = captureUnsupportedWhenNeeded(
        UnsupportedRenderCapability.BLEND_MODE,
        delegate.supportsBlendMode(),
    )

    /**
     * 开启 blend 组捕获（#8 mix-blend-mode）：压入一个独立子命令缓冲并记录混合模式序数。
     * push..pop 之间的所有绘制经 [addRecord] 改进该缓冲（与 filter 组同构）。
     */
    override fun pushBlendMode(mode: Int) {
        blendGroupStack.addFirst(BlendGroupCapture(mode, ++captureDepthCounter))
    }

    /**
     * 结束 blend 组捕获：把栈顶捕获缓冲封装为一条 [RenderCommandType.BLEND_GROUP] 记录，
     * 经 [addRecord] 放回外层目标。子命令为空时仍记录一条空 BLEND_GROUP（回放走
     * pushBlendMode→无子命令→popBlendMode，无副作用），避免静默吞掉。
     */
    override fun popBlendMode() {
        val capture = blendGroupStack.removeFirstOrNull()
        if (capture == null) {
            log.warn("popBlendMode 调用时 blend 组捕获栈为空，忽略")
            return
        }
        addRecord(
            DrawRecord.builder()
                .type(RenderCommandType.BLEND_GROUP)
                .blendMode(capture.mode)
                .blendGroupCommands(capture.commands)
                .sequence(sequence++)
                .state(snapshotState())
                .build(),
        )
    }

    override fun pushTransform(tx: Float, ty: Float, scaleX: Float, scaleY: Float) {
        stateStacks.pushTransform(tx, ty, scaleX, scaleY)
    }

    override fun popTransform() {
        stateStacks.popTransform()
    }

    override fun pushTransform3x3(mat3x3: FloatArray) {
        stateStacks.pushTransform3x3(mat3x3)
    }

    override fun popTransform3x3() {
        // 三种变换共栈（与拆分前一致）
        stateStacks.popTransform()
    }

    override fun pushTransform4x4(mat4x4: FloatArray) {
        stateStacks.pushTransform4x4(mat4x4)
    }

    override fun popTransform4x4() {
        // 三种变换共栈（与拆分前一致）
        stateStacks.popTransform()
    }

    /** 层缓冲模式下 z 偏移无意义（层序已解决遮挡），空实现。 */
    override fun pushZ(z: Float) {
        // 层缓冲模式下无需 z 偏移
    }

    override fun popZ() {
        // 层缓冲模式下无需 z 偏移
    }

    override fun flush() {
        // 层缓冲模式下 flush 为 no-op，由 endFrame 统一回放
    }

    // ===== 绘制方法 —— 缓冲到对应层 =====

    override fun fillRect(x: Float, y: Float, width: Float, height: Float, argb: Int) {
        addRecord(
            DrawRecord.builder()
                .type(RenderCommandType.FILL_RECT)
                .pos(x, y, width, height)
                .argb(argb)
                .sequence(sequence++)
                .state(snapshotState())
                .build(),
        )
    }

    override fun drawText(text: String, x: Float, y: Float, argb: Int, scale: Float) {
        // #51：4 参缓冲时即同步解析 FontAccess 环境层（此刻 renderRecursive 仍在栈中，元素字体层有效），
        // 把字体名烤进 DrawRecord、回放走 9 参分支。否则到 endFrame() 回放时元素字体层已 pop，
        // button/textfield/tabview/toast 等用 4 参 drawText 的控件自身 font 丢失、只剩页面/全局。
        // 与 9 参 drawText 缓冲对齐，让“4 参自动继承元素字体”的设计在层缓冲下真正生效，
        // 也消除按钮测宽用元素字体、绘制却退默认字体的不一致。
        val font = FontAccess.resolve(null)
        addRecord(
            DrawRecord.builder()
                .type(RenderCommandType.DRAW_TEXT)
                .pos(x, y, 0f, 0f)
                .argb(argb)
                .text(text)
                .scale(scale)
                .fontName(font.effectiveName())
                .shadow(font.effectiveShadow())
                .bold(font.effectiveBold())
                .italic(font.effectiveItalic())
                .sequence(sequence++)
                .state(snapshotState())
                .build(),
        )
    }

    override fun drawText(
        text: String,
        x: Float,
        y: Float,
        argb: Int,
        scale: Float,
        fontName: String?,
        shadow: Boolean,
        bold: Boolean,
        italic: Boolean,
    ) {
        addRecord(
            DrawRecord.builder()
                .type(RenderCommandType.DRAW_TEXT)
                .pos(x, y, 0f, 0f)
                .argb(argb)
                .text(text)
                .scale(scale)
                .fontName(fontName)
                .shadow(shadow)
                .bold(bold)
                .italic(italic)
                .sequence(sequence++)
                .state(snapshotState())
                .build(),
        )
    }

    /**
     * 录一条 [RenderCommandType.DRAW_RICH_TEXT] 记录（内联富文本，含 `[kind:id]` 图标标签）。
     *
     * 必须覆盖：否则富文本绘制走 [UiRenderBridge] 接口 default 剥标签降级为纯文本，回放时丢内联图标。
     * 与 [drawText] 平行——文本/坐标/颜色/字体/粗斜照录，额外携带 maxWidth/multiline/lineStep，
     * 回放经 [replayDrawCall] 透传到 `target.drawRichText(...)`。
     */
    override fun drawRichText(
        text: String,
        x: Float,
        y: Float,
        argb: Int,
        scale: Float,
        fontName: String?,
        shadow: Boolean,
        bold: Boolean,
        italic: Boolean,
        maxWidth: Float,
        multiline: Boolean,
        lineStep: Float,
    ) {
        addRecord(
            DrawRecord.builder()
                .type(RenderCommandType.DRAW_RICH_TEXT)
                .pos(x, y, 0f, 0f)
                .argb(argb)
                .text(text)
                .scale(scale)
                .fontName(fontName)
                .shadow(shadow)
                .bold(bold)
                .italic(italic)
                .richTextMaxWidth(maxWidth)
                .richTextMultiline(multiline)
                .richTextLineStep(lineStep)
                .sequence(sequence++)
                .state(snapshotState())
                .build(),
        )
    }

    override fun drawLine(x1: Float, y1: Float, x2: Float, y2: Float, thickness: Float, argb: Int) {
        addRecord(
            DrawRecord.builder()
                .type(RenderCommandType.DRAW_LINE)
                .pos(x1, y1, x2, y2)
                .argb(argb)
                .lineThickness(thickness)
                .sequence(sequence++)
                .state(snapshotState())
                .build(),
        )
    }

    override fun drawImage(resource: String, x: Float, y: Float, width: Float, height: Float) {
        addRecord(
            DrawRecord.builder()
                .type(RenderCommandType.DRAW_IMAGE)
                .pos(x, y, width, height)
                .texture(resource)
                .sequence(sequence++)
                .state(snapshotState())
                .build(),
        )
    }

    override fun drawImageStyled(resource: String, x: Float, y: Float, w: Float, h: Float, style: ImageStyle?) {
        addRecord(
            DrawRecord.builder()
                .type(RenderCommandType.IMAGE_STYLED)
                .pos(x, y, w, h)
                .texture(resource)
                .imageStyle(style)
                .sequence(sequence++)
                .state(snapshotState())
                .build(),
        )
    }

    override fun drawTexturedQuad(
        resource: String,
        x: Float,
        y: Float,
        width: Float,
        height: Float,
        uvX: Float,
        uvY: Float,
        uvW: Float,
        uvH: Float,
    ) {
        addRecord(
            DrawRecord.builder()
                .type(RenderCommandType.DRAW_TEXTURED_QUAD)
                .pos(x, y, width, height)
                .texture(resource)
                .uv(uvX, uvY, uvW, uvH)
                .sequence(sequence++)
                .state(snapshotState())
                .build(),
        )
    }

    override fun drawItemStack(itemId: String, count: Int, x: Float, y: Float, width: Float, height: Float) {
        addRecord(
            DrawRecord.builder()
                .type(RenderCommandType.DRAW_ITEM_STACK)
                .pos(x, y, width, height)
                .itemId(itemId)
                .itemCount(count)
                .sequence(sequence++)
                .state(snapshotState())
                .build(),
        )
    }

    override fun drawItemStack(
        itemId: String,
        count: Int,
        enchanted: Boolean,
        x: Float,
        y: Float,
        width: Float,
        height: Float,
    ) {
        addRecord(
            DrawRecord.builder()
                .type(RenderCommandType.DRAW_ITEM_STACK)
                .pos(x, y, width, height)
                .itemId(itemId)
                .itemCount(count)
                .enchanted(enchanted)
                .sequence(sequence++)
                .state(snapshotState())
                .build(),
        )
    }

    override fun drawItemStack(
        itemId: String,
        count: Int,
        enchanted: Boolean,
        showCount: Boolean,
        x: Float,
        y: Float,
        width: Float,
        height: Float,
    ) {
        addRecord(
            DrawRecord.builder()
                .type(RenderCommandType.DRAW_ITEM_STACK)
                .pos(x, y, width, height)
                .itemId(itemId)
                .itemCount(count)
                .enchanted(enchanted)
                .showCount(showCount)
                .sequence(sequence++)
                .state(snapshotState())
                .build(),
        )
    }

    override fun drawItemStack(
        itemId: String,
        count: Int,
        enchanted: Boolean,
        showCount: Boolean,
        countAnchor: String?,
        countOffsetX: Float,
        countOffsetY: Float,
        x: Float,
        y: Float,
        width: Float,
        height: Float,
    ) {
        addRecord(
            DrawRecord.builder()
                .type(RenderCommandType.DRAW_ITEM_STACK)
                .pos(x, y, width, height)
                .itemId(itemId)
                .itemCount(count)
                .enchanted(enchanted)
                .showCount(showCount)
                .countAnchor(countAnchor)
                .countOffsetX(countOffsetX)
                .countOffsetY(countOffsetY)
                .sequence(sequence++)
                .state(snapshotState())
                .build(),
        )
    }

    override fun drawItemStack(
        itemId: String,
        itemNbt: String?,
        count: Int,
        enchanted: Boolean,
        showCount: Boolean,
        countAnchor: String?,
        countOffsetX: Float,
        countOffsetY: Float,
        x: Float,
        y: Float,
        width: Float,
        height: Float,
    ) {
        addRecord(
            DrawRecord.builder()
                .type(RenderCommandType.DRAW_ITEM_STACK)
                .pos(x, y, width, height)
                .itemId(itemId)
                .itemNbt(itemNbt)
                .itemCount(count)
                .enchanted(enchanted)
                .showCount(showCount)
                .countAnchor(countAnchor)
                .countOffsetX(countOffsetX)
                .countOffsetY(countOffsetY)
                .sequence(sequence++)
                .state(snapshotState())
                .build(),
        )
    }

    // 13 参 backdropColor 重载（issue #68）：必须显式覆盖，否则 13 参调用会走 UiRenderBridge default 13→12
    // 丢掉 backdropColor，缓冲记录与回放都拿不到背景遮罩色，3D/贴图/bbmodel 物品仍不随 opacity 变暗。
    override fun drawItemStack(
        itemId: String,
        itemNbt: String?,
        count: Int,
        enchanted: Boolean,
        showCount: Boolean,
        countAnchor: String?,
        countOffsetX: Float,
        countOffsetY: Float,
        backdropColor: Int,
        x: Float,
        y: Float,
        width: Float,
        height: Float,
    ) {
        addRecord(
            DrawRecord.builder()
                .type(RenderCommandType.DRAW_ITEM_STACK)
                .pos(x, y, width, height)
                .itemId(itemId)
                .itemNbt(itemNbt)
                .itemCount(count)
                .enchanted(enchanted)
                .showCount(showCount)
                .countAnchor(countAnchor)
                .countOffsetX(countOffsetX)
                .countOffsetY(countOffsetY)
                .backdropColor(backdropColor)
                .sequence(sequence++)
                .state(snapshotState())
                .build(),
        )
    }

    override fun drawEntityPreview(
        entityRef: String,
        x: Float,
        y: Float,
        width: Float,
        height: Float,
        mouseX: Float,
        mouseY: Float,
    ) {
        addRecord(
            DrawRecord.builder()
                .type(RenderCommandType.DRAW_ENTITY_PREVIEW)
                .pos(x, y, width, height)
                .entityRef(entityRef)
                .mouseX(mouseX)
                .mouseY(mouseY)
                .sequence(sequence++)
                .state(snapshotState())
                .build(),
        )
    }

    // 12 参兼容版：转发到 13 参主路径（entityName=null）。
    override fun drawEntityPreview(
        entityRef: String,
        entityType: String?,
        x: Float,
        y: Float,
        width: Float,
        height: Float,
        mouseX: Float,
        mouseY: Float,
        scale: Float,
        rotationX: Float,
        rotationY: Float,
        followMouse: Boolean,
    ) {
        drawEntityPreview(
            entityRef, entityType, null,
            x, y, width, height, mouseX, mouseY,
            scale, rotationX, rotationY, followMouse,
        )
    }

    // 13 参主路径：必须显式覆盖，否则 13 参调用会走 UiRenderBridge default 13→12 丢掉 entityName，
    // 缓冲记录与回放都拿不到名称，预览虚拟实体不被命名，实体档案「按名称匹配」失效。
    override fun drawEntityPreview(
        entityRef: String,
        entityType: String?,
        entityName: String?,
        x: Float,
        y: Float,
        width: Float,
        height: Float,
        mouseX: Float,
        mouseY: Float,
        scale: Float,
        rotationX: Float,
        rotationY: Float,
        followMouse: Boolean,
    ) {
        addRecord(
            DrawRecord.builder()
                .type(RenderCommandType.DRAW_ENTITY_PREVIEW)
                .pos(x, y, width, height)
                .entityRef(entityRef)
                .entityType(entityType)
                .entityName(entityName)
                .mouseX(mouseX)
                .mouseY(mouseY)
                .entityScale(scale)
                .rotationX(rotationX)
                .rotationY(rotationY)
                .followMouse(followMouse)
                .sequence(sequence++)
                .state(snapshotState())
                .build(),
        )
    }

    override fun drawModelPreview(
        source: String,
        modelBase: String,
        texture: String?,
        instanceKey: String,
        x: Float,
        y: Float,
        width: Float,
        height: Float,
        scale: Float,
        rotationX: Float,
        rotationY: Float,
        interactive: Boolean,
        backgroundColor: Int,
    ) {
        addRecord(
            DrawRecord.builder()
                .type(RenderCommandType.DRAW_MODEL_PREVIEW)
                .pos(x, y, width, height)
                .modelSource(source)
                .modelBase(modelBase)
                .modelTexture(texture)
                .modelInstanceKey(instanceKey)
                .modelScale(scale)
                .modelRotationX(rotationX)
                .modelRotationY(rotationY)
                .modelInteractive(interactive)
                .modelBackgroundColor(backgroundColor)
                .sequence(sequence++)
                .state(snapshotState())
                .build(),
        )
    }

    override fun submitBatch(batch: RenderBatch) {
        for (cmd in batch.commands) {
            val builder = DrawRecord.builder()
                .type(cmd.type)
                // 保留命令原件：styled 类型（ROUNDED_RECT/GRADIENT/SHADOW）回放时重建 batch，
                // 保留圆角/边框/渐变/阴影/遮罩，而非降级成纯色 fillRect。
                .command(cmd)
                .pos(cmd.x, cmd.y, cmd.w, cmd.h)
                .argb(cmd.argb)
                .sequence(sequence++)
                .state(snapshotState())

            // 根据命令类型设置对应的附加字段
            when (cmd.type) {
                RenderCommandType.DRAW_TEXT -> builder.text(cmd.text).scale(cmd.scale)
                RenderCommandType.DRAW_LINE -> builder.lineThickness(1f)
                RenderCommandType.DRAW_IMAGE,
                RenderCommandType.DRAW_TEXTURED_QUAD,
                RenderCommandType.IMAGE_STYLED,
                -> builder.texture(cmd.texture)
                RenderCommandType.DRAW_ITEM_STACK ->
                    builder.itemId(cmd.itemId).itemNbt(cmd.itemNbt).itemCount(cmd.itemCount)
                RenderCommandType.DRAW_ENTITY_PREVIEW ->
                    builder.entityRef(cmd.entityRef).mouseX(cmd.mouseX).mouseY(cmd.mouseY)
                else -> Unit
            }
            if (cmd.type == RenderCommandType.DRAW_TEXTURED_QUAD) {
                builder.uv(cmd.uvX, cmd.uvY, cmd.uvW, cmd.uvH)
            }
            if (cmd.type == RenderCommandType.IMAGE_STYLED) {
                builder.imageStyle(cmd.imageStyle)
            }
            addRecord(builder.build())
        }
    }

    // ===== 特效方法 —— 缓冲到当前层 =====

    override fun applyNeonGlow(
        x: Float,
        y: Float,
        width: Float,
        height: Float,
        neonColor: Int,
        intensity: Float,
        size: Float,
        animated: Boolean,
        cornerRadii: FloatArray?,
    ) {
        addRecord(
            DrawRecord.builder()
                .type(RenderCommandType.NEON_GLOW)
                .pos(x, y, width, height)
                .argb(neonColor)
                .effectIntensity(intensity)
                .effectSize(size)
                .effectAnimated(animated)
                .effectCornerRadii(cornerRadii)
                .sequence(sequence++)
                .state(snapshotState())
                .build(),
        )
    }

    override fun applyRippleEffect(
        x: Float,
        y: Float,
        width: Float,
        height: Float,
        color: Int,
        progress: Float,
        radius: Float,
        centerX: Float,
        centerY: Float,
    ) {
        addRecord(
            DrawRecord.builder()
                .type(RenderCommandType.RIPPLE)
                .pos(x, y, width, height)
                .argb(color)
                .effectProgress(progress)
                .effectRadius(radius)
                .effectCenterX(centerX)
                .effectCenterY(centerY)
                .sequence(sequence++)
                .state(snapshotState())
                .build(),
        )
    }

    override fun applyRippleEffect(
        x: Float,
        y: Float,
        width: Float,
        height: Float,
        color: Int,
        progress: Float,
        radius: Float,
        centerX: Float,
        centerY: Float,
        cornerRadii: FloatArray?,
    ) {
        addRecord(
            DrawRecord.builder()
                .type(RenderCommandType.RIPPLE)
                .pos(x, y, width, height)
                .argb(color)
                .effectProgress(progress)
                .effectRadius(radius)
                .effectCenterX(centerX)
                .effectCenterY(centerY)
                .effectCornerRadii(cornerRadii)
                .sequence(sequence++)
                .state(snapshotState())
                .build(),
        )
    }

    override fun applyProceduralTexture(
        x: Float,
        y: Float,
        width: Float,
        height: Float,
        kind: String?,
        colorA: Int,
        colorB: Int,
        scale: Float,
        density: Float,
        intensity: Float,
        cornerRadii: FloatArray?,
    ) {
        addRecord(
            DrawRecord.builder()
                .type(RenderCommandType.PROCEDURAL_TEXTURE)
                .pos(x, y, width, height)
                .argb(colorA)
                .effectKind(kind)
                .effectColorA(colorA)
                .effectColorB(colorB)
                .effectIntensity(intensity)
                .effectDensity(density)
                .effectSize(scale)
                .effectCornerRadii(cornerRadii)
                .sequence(sequence++)
                .state(snapshotState())
                .build(),
        )
    }

    override fun applyFlow(
        x: Float,
        y: Float,
        width: Float,
        height: Float,
        flowColor: Int,
        speedHz: Float,
        widthPx: Float,
        angleDeg: Float,
        mode: Int,
        cornerRadii: FloatArray?,
    ) {
        addRecord(
            DrawRecord.builder()
                .type(RenderCommandType.FLOW)
                .pos(x, y, width, height)
                .argb(flowColor)
                .effectSpeed(speedHz)
                .effectSize(widthPx)
                .effectFrequency(angleDeg)
                .effectMode(mode)
                .effectCornerRadii(cornerRadii)
                .sequence(sequence++)
                .state(snapshotState())
                .build(),
        )
    }

    override fun applyEnergy(
        x: Float,
        y: Float,
        width: Float,
        height: Float,
        energyColor: Int,
        intensity: Float,
        frequency: Float,
        speed: Float,
        pattern: Int,
        cornerRadii: FloatArray?,
    ) {
        addRecord(
            DrawRecord.builder()
                .type(RenderCommandType.ENERGY)
                .pos(x, y, width, height)
                .argb(energyColor)
                .effectIntensity(intensity)
                .effectFrequency(frequency)
                .effectSpeed(speed)
                .effectMode(pattern)
                .effectCornerRadii(cornerRadii)
                .sequence(sequence++)
                .state(snapshotState())
                .build(),
        )
    }

    override fun drawMaterialLayer(kind: String, fields: Map<String, *>, x: Float, y: Float, w: Float, h: Float) {
        addRecord(
            DrawRecord.builder()
                .type(RenderCommandType.MATERIAL_LAYER)
                .pos(x, y, w, h)
                .materialKind(kind)
                .materialFields(fields.toMap())
                .sequence(sequence++)
                .state(snapshotState())
                .build(),
        )
    }

    override fun drawParticleLayer(
        elementId: String,
        ordinal: Int,
        spec: Map<String, *>,
        x: Float,
        y: Float,
        w: Float,
        h: Float,
    ) {
        addRecord(
            DrawRecord.builder()
                .type(RenderCommandType.PARTICLE)
                .pos(x, y, w, h)
                .particleElementId(elementId)
                .particleOrdinal(ordinal)
                .particleSpec(spec.toMap())
                .sequence(sequence++)
                .state(snapshotState())
                .build(),
        )
    }

    // ===== 平台能力查询 —— 委托给 delegate =====

    override fun supportsRuntimeTransform(): Boolean = delegate.supportsRuntimeTransform()

    override fun supportsRuntimeAlpha(): Boolean = delegate.supportsRuntimeAlpha()

    // 富文本能力查询：纯查询不录制，直转 delegate；否则走接口 default false 令上层降级纯文本丢内联图标。
    override fun supportsRichText(): Boolean = delegate.supportsRichText()

    override fun supportsColorMatrix(): Boolean = delegate.supportsColorMatrix()

    override fun supportsFullTransform(): Boolean = delegate.supportsFullTransform()

    private fun captureUnsupportedWhenNeeded(
        capability: UnsupportedRenderCapability,
        supported: Boolean,
    ): Boolean {
        if (!supported) {
            captureUnsupportedCapability(capability)
        }
        return supported
    }

    override fun supportsBatching(): Boolean = delegate.supportsBatching()

    // ===== 查询方法 —— 转发到 delegate =====

    override fun textWidth(text: String, scale: Float): Float = delegate.textWidth(text, scale)

    override fun textWidth(text: String, scale: Float, fontName: String?, bold: Boolean): Float =
        delegate.textWidth(text, scale, fontName, bold)

    // 富文本度量：纯查询不录制，参数原样转发到 delegate（用真实平台字体度量内联图文混排尺寸）。
    override fun measureRichText(
        text: String,
        scale: Float,
        fontName: String?,
        bold: Boolean,
        maxWidth: Float,
        multiline: Boolean,
    ): FloatArray = delegate.measureRichText(text, scale, fontName, bold, maxWidth, multiline)

    // screen-space 模式下返回未偏移宽度，使 fullscreen/centered modal 的全屏 backdrop 与居中正确；
    // screen-space tooltip 同走未偏移屏幕坐标系（候选 box 已是屏幕绝对坐标），放置边界用全屏尺寸。
    override fun screenWidth(): Float =
        (if (screenSpaceMode || screenSpaceTooltipMode) screenDelegate else delegate).screenWidth()

    override fun screenHeight(): Float =
        (if (screenSpaceMode || screenSpaceTooltipMode) screenDelegate else delegate).screenHeight()

    override fun textureExists(resource: String): Boolean = delegate.textureExists(resource)

    override fun screenSpaceBridge(): UiRenderBridge = delegate.screenSpaceBridge()

    // ===== 内部回放 =====

    /**
     * 将一条 DrawRecord 回放到 target（普通层走 delegate，screen-space 浮层走 screenDelegate）。
     */
    @Suppress("CyclomaticComplexMethod", "LongMethod")
    private fun replayDrawCall(target: UiRenderBridge, record: DrawRecord) {
        when (record.type()) {
            RenderCommandType.FILL_RECT ->
                target.fillRect(record.x(), record.y(), record.w(), record.h(), record.argb())
            RenderCommandType.DRAW_TEXT -> {
                val fontName = record.fontName()
                if (fontName != null) {
                    target.drawText(
                        record.text().orEmpty(), record.x(), record.y(), record.argb(), record.scale(),
                        fontName, record.shadow(), record.bold(), record.italic(),
                    )
                } else {
                    target.drawText(record.text().orEmpty(), record.x(), record.y(), record.argb(), record.scale())
                }
            }
            RenderCommandType.DRAW_RICH_TEXT -> target.drawRichText(
                record.text().orEmpty(), record.x(), record.y(),
                record.argb(), record.scale(),
                record.fontName(), record.shadow(), record.bold(), record.italic(),
                record.richTextMaxWidth(), record.richTextMultiline(), record.richTextLineStep(),
            )
            RenderCommandType.DRAW_LINE -> target.drawLine(
                record.x(),
                record.y(),
                record.w(),
                record.h(),
                record.lineThickness(),
                record.argb(),
            )
            RenderCommandType.DRAW_IMAGE ->
                target.drawImage(record.texture().orEmpty(), record.x(), record.y(), record.w(), record.h())
            RenderCommandType.IMAGE_STYLED -> target.drawImageStyled(
                record.texture().orEmpty(),
                record.x(),
                record.y(),
                record.w(),
                record.h(),
                record.imageStyle(),
            )
            RenderCommandType.DRAW_TEXTURED_QUAD -> target.drawTexturedQuad(
                record.texture().orEmpty(),
                record.x(), record.y(), record.w(), record.h(),
                record.uvX(), record.uvY(), record.uvW(), record.uvH(),
            )
            RenderCommandType.DRAW_ITEM_STACK -> target.drawItemStack(
                record.itemId().orEmpty(), record.itemNbt(), record.itemCount(),
                record.enchanted(), record.showCount(),
                record.countAnchor(), record.countOffsetX(), record.countOffsetY(),
                record.backdropColor(),
                record.x(), record.y(), record.w(), record.h(),
            )
            RenderCommandType.DRAW_ENTITY_PREVIEW -> target.drawEntityPreview(
                record.entityRef().orEmpty(), record.entityType(), record.entityName(),
                record.x(), record.y(), record.w(), record.h(),
                record.mouseX(), record.mouseY(),
                record.entityScale(), record.rotationX(), record.rotationY(), record.followMouse(),
            )
            RenderCommandType.DRAW_MODEL_PREVIEW -> target.drawModelPreview(
                record.modelSource().orEmpty(), record.modelBase().orEmpty(),
                record.modelTexture(), record.modelInstanceKey().orEmpty(),
                record.x(), record.y(), record.w(), record.h(),
                record.modelScale(), record.modelRotationX(), record.modelRotationY(),
                record.modelInteractive(), record.modelBackgroundColor(),
            )
            RenderCommandType.ROUNDED_RECT -> replayStyledBatch(target, record)
            RenderCommandType.NEON_GLOW -> target.applyNeonGlow(
                record.x(), record.y(), record.w(), record.h(),
                record.argb(), record.effectIntensity(), record.effectSize(),
                record.effectAnimated(), record.effectCornerRadii(),
            )
            RenderCommandType.RIPPLE -> {
                val cornerRadii = record.effectCornerRadii()
                if (cornerRadii != null) {
                    target.applyRippleEffect(
                        record.x(), record.y(), record.w(), record.h(),
                        record.argb(), record.effectProgress(), record.effectRadius(),
                        record.effectCenterX(), record.effectCenterY(), cornerRadii,
                    )
                } else {
                    target.applyRippleEffect(
                        record.x(), record.y(), record.w(), record.h(),
                        record.argb(), record.effectProgress(), record.effectRadius(),
                        record.effectCenterX(), record.effectCenterY(),
                    )
                }
            }
            RenderCommandType.PROCEDURAL_TEXTURE -> target.applyProceduralTexture(
                record.x(), record.y(), record.w(), record.h(),
                record.effectKind(), record.effectColorA(), record.effectColorB(),
                record.effectSize(), record.effectDensity(), record.effectIntensity(),
                record.effectCornerRadii(),
            )
            RenderCommandType.FLOW -> target.applyFlow(
                record.x(), record.y(), record.w(), record.h(),
                record.argb(), record.effectSpeed(), record.effectSize(),
                record.effectFrequency(), record.effectMode(), record.effectCornerRadii(),
            )
            RenderCommandType.ENERGY -> target.applyEnergy(
                record.x(), record.y(), record.w(), record.h(),
                record.argb(), record.effectIntensity(), record.effectFrequency(),
                record.effectSpeed(), record.effectMode(), record.effectCornerRadii(),
            )
            RenderCommandType.MATERIAL_LAYER -> {
                val kind = record.materialKind()
                if (kind != null) {
                    target.drawMaterialLayer(
                        kind,
                        record.materialFields().orEmpty(),
                        record.x(),
                        record.y(),
                        record.w(),
                        record.h(),
                    )
                }
            }
            RenderCommandType.PARTICLE -> target.drawParticleLayer(
                record.particleElementId(),
                record.particleOrdinal(),
                record.particleSpec().orEmpty(),
                record.x(),
                record.y(),
                record.w(),
                record.h(),
            )
            RenderCommandType.GRADIENT, RenderCommandType.SHADOW -> replayStyledBatch(target, record)
            RenderCommandType.FILTER_GROUP -> replayFilterGroup(target, record)
            RenderCommandType.BACKDROP_FILTER -> replayBackdrop(target, record)
            RenderCommandType.BLEND_GROUP -> replayBlendGroup(target, record)
            RenderCommandType.HISTORY_GROUP -> replayHistoryGroup(target, record)
            else -> {
                // 未知类型：降级为 fillRect 兜底
                log.warn("回放未实现的 DrawRecord 类型: ${record.type()}，坐标=(${record.x()},${record.y()})，降级为 fillRect")
                target.fillRect(record.x(), record.y(), record.w(), record.h(), record.argb())
            }
        }
    }

    /**
     * 回放 styled 批命令（ROUNDED_RECT / GRADIENT / SHADOW）：用保留的原始 [RenderCommand]
     * 重建单命令 [RenderBatch] 走 `target.submitBatch`，保留圆角/边框/渐变/阴影/遮罩。
     * 原始命令缺失（理论不可达）时降级为纯色 fillRect。
     */
    private fun replayStyledBatch(target: UiRenderBridge, record: DrawRecord) {
        val cmd = record.command()
        if (cmd == null) {
            target.fillRect(record.x(), record.y(), record.w(), record.h(), record.argb())
            return
        }
        target.submitBatch(RenderBatch(cmd.type, null, RenderCommand.NO_CLIP, listOf(cmd)))
    }

    /**
     * 回放 filter 组（CSS filter: blur）：
     * 1. `delegate.beginFilterGroup(radius, x, y, w, h)`：平台层 acquire 离屏 FBO、
     *    设坐标平移、把绘制重定向到离屏（acquire 失败时平台层内部转直通）；
     * 2. 逐条回放捕获的子命令（各自携带录制时的 StateSnapshot），此时绘制落到离屏；
     * 3. `delegate.flush()` 确保延迟子命令落到离屏 FBO；
     * 4. `delegate.endFilterGroup()`：两 pass 高斯 + 合成回主屏 + 复位主目标。
     *
     * 平台不支持 filter 组（[UiRenderBridge.supportsFilterGroup] 为 false）时直接回放子命令，
     * 不模糊、不崩、不丢内容（与无 blur 渲染逐像素一致，零回归）。
     */
    private fun replayFilterGroup(target: UiRenderBridge, record: DrawRecord) {
        val sub = record.filterGroupCommands() ?: emptyList()
        val supported = target.supportsFilterGroup()
        if (supported) {
            target.beginFilterGroup(record.blurRadius(), record.x(), record.y(), record.w(), record.h())
        }
        try {
            sub.forEach { replayRecordWithState(target, it) }
        } finally {
            if (supported) {
                // flush 让延迟子命令落到离屏 FBO，再做两 pass 高斯并复位主目标。
                target.flush()
                target.endFilterGroup()
            }
        }
    }

    /**
     * 回放 backdrop-filter 背景模糊（#8）：
     *
     * 先 `target.flush()`（C1 时序：保证同层及更早层的延迟内容已落主帧缓冲，否则 backdrop
     * 采样到空），再 `target.applyBackdropBlur(...)` 就地 blit 背景做高斯画回元素 rect。
     *
     * 注：调用方 [replayBuffer]/[replayFilterGroup] 已对 [isImmediateRecord]
     * 为 true 的记录在回放前 flush 一次；此处再补一次 flush 以确保 backdrop 单独回放路径（如在 blend 组内）
     * 也满足时序。平台不支持（[UiRenderBridge.supportsBackdropFilter] 为 false）时
     * applyBackdropBlur 为 no-op，元素照常清晰渲染（零回归）。
     */
    private fun replayBackdrop(target: UiRenderBridge, record: DrawRecord) {
        target.flush()
        target.applyBackdropBlur(record.backdropBlurRadius(), record.x(), record.y(), record.w(), record.h())
    }

    /**
     * 回放 blend 组（#8 mix-blend-mode）：
     * 1. `target.pushBlendMode(mode)`：平台层记录当前混合函数并切到目标 GL 混合函数；
     * 2. 逐条回放捕获的子命令（各自携带录制时的 StateSnapshot），落盘时按目标混合函数合成；
     * 3. `target.flush()` 确保延迟子命令在还原混合函数前提交；
     * 4. `target.popBlendMode()`：还原默认混合函数。
     *
     * 平台不支持（[UiRenderBridge.supportsBlendMode] 为 false）时直接回放子命令，
     * 不切混合函数、不崩、不丢内容（与 NORMAL 混合逐像素一致，零回归）。
     */
    private fun replayBlendGroup(target: UiRenderBridge, record: DrawRecord) {
        val sub = record.blendGroupCommands() ?: emptyList()
        val supported = target.supportsBlendMode()
        if (supported) {
            target.pushBlendMode(record.blendMode())
        }
        try {
            sub.forEach { replayRecordWithState(target, it) }
        } finally {
            if (supported) {
                // flush 让延迟子命令在还原混合函数前按目标混合函数提交，避免混合函数作用错批次。
                target.flush()
                target.popBlendMode()
            }
        }
    }

    /**
     * 回放 history 组（FR-UI-023 historyLayer）：
     * 1. `target.beginHistoryCapture(elementId, spec, x,y,w,h)`：平台层按 source=self 将元素绘制重定向到离屏隔离靶；
     * 2. 逐条回放捕获的子命令（各自携带录制时的 StateSnapshot），此时绘制落到离屏靶（self）或主屏（backdrop）；
     * 3. `target.flush()` 确保延迟子命令落到隔离靶；
     * 4. `target.endHistoryCapture()`：捕获元素自身像素入环形缓冲、叠加历史余晖合成回主屏、复位主目标。
     *
     * 平台不支持（[UiRenderBridge.supportsHistoryCapture] 为 false）时直接回放子命令，
     * 不捕获、不崩、不丢内容（与无 historyLayer 逐像素一致，零回归）。
     */
    private fun replayHistoryGroup(target: UiRenderBridge, record: DrawRecord) {
        val sub = record.historyGroupCommands().orEmpty()
        val supported = target.supportsHistoryCapture()
        if (supported) {
            target.beginHistoryCapture(
                record.historyElementId(),
                record.historySpec().orEmpty(),
                record.x(),
                record.y(),
                record.w(),
                record.h(),
            )
        }
        var failure: Throwable? = null
        try {
            sub.forEach { replayRecordWithState(target, it) }
        } catch (t: Throwable) {
            failure = t
            throw t
        } finally {
            if (supported) {
                var cleanupFailure: Throwable? = null
                try {
                    target.flush()
                } catch (t: Throwable) {
                    cleanupFailure = t
                }
                try {
                    target.endHistoryCapture()
                } catch (t: Throwable) {
                    if (cleanupFailure == null) cleanupFailure = t else cleanupFailure.addSuppressed(t)
                }
                cleanupFailure?.let { cleanup ->
                    if (failure == null) throw cleanup else failure.addSuppressed(cleanup)
                }
            }
        }
    }


    /**
     * 将快照中的非默认状态推入 target，使回放时的渲染状态与录制时一致。
     *
     * 仅推入非默认值：跳过空变换栈、空透明度栈、null 裁剪。
     *
     * **必须逆列表序推入**：快照各栈列表的首元素是**最新入栈项**（见 [StateSnapshot] 栈序约定），
     * 而录制时是「外层先入、内层后入」。委托端（如 Fabric 的 `PoseStack`）按后乘合成
     * `pose = pose × T`，故只有从列表末尾（最外层）向前推，才能复原录制时的合成序
     * `T_外 × T_内`；正序推得到的是 `T_内 × T_外`——两者对「父 scale/rotate + 子 translate」
     * 这类不可交换组合会把内容画到不同位置。色彩矩阵同理（矩阵乘法不可交换）。
     * 透明度是标量连乘、可交换，一并逆序只为维持「重放序 == 录制序」这条统一不变量。
     */
    private fun pushStateToDelegate(target: UiRenderBridge, state: StateSnapshot) {
        val transforms = state.transformStack
        for (i in transforms.indices.reversed()) {
            when (val entry = transforms[i]) {
                is TransformEntry.TranslateScale ->
                    target.pushTransform(entry.tx, entry.ty, entry.sx, entry.sy)
                is TransformEntry.Matrix3x3 -> target.pushTransform3x3(entry.mat)
                is TransformEntry.Matrix4x4 -> target.pushTransform4x4(entry.mat)
            }
        }
        val alphas = state.alphaStack
        for (i in alphas.indices.reversed()) {
            target.pushAlpha(alphas[i])
        }
        val matrices = state.colorMatrixStack
        for (i in matrices.indices.reversed()) {
            target.pushColorMatrix(matrices[i])
        }
        state.currentClip?.let { c ->
            target.pushClip(c[0], c[1], c[2], c[3])
        }
    }

    /**
     * 弹出 [pushStateToDelegate] 推入的非默认状态，严格按其逆序。
     *
     * 变换栈逆列表序推入（末元素最先推），故此处按列表正序弹出（首元素最先弹）。
     */
    private fun popStateFromDelegate(target: UiRenderBridge, state: StateSnapshot) {
        if (state.currentClip != null) {
            target.popClip()
        }
        repeat(state.colorMatrixStack.size) { target.popColorMatrix() }
        repeat(state.alphaStack.size) { target.popAlpha() }
        for (entry in state.transformStack) {
            when (entry) {
                is TransformEntry.Matrix4x4 -> target.popTransform4x4()
                is TransformEntry.Matrix3x3 -> target.popTransform3x3()
                else -> target.popTransform()
            }
        }
    }

    private class UnsupportedContextScope(
        val phase: DrawTracePhase,
        val layerId: Int?,
    ) {
        val capabilities = linkedSetOf<UnsupportedRenderCapability>()
    }

    /**
     * filter 组（CSS filter: blur）捕获上下文：保存模糊半径、合成矩形与已捕获的子命令列表。
     * 由 [beginFilterGroup] 创建并压入 [filterGroupStack]，[endFilterGroup] 弹出。
     *
     * @property depth 入栈深度（[captureDepthCounter] 自增值），供 [innermostCaptureBuffer] 在两栈交叠时判最内层。
     */
    private class FilterGroupCapture(
        val blurRadius: Float,
        val x: Float,
        val y: Float,
        val w: Float,
        val h: Float,
        val depth: Int,
    ) {
        val commands: MutableList<DrawRecord> = mutableListOf()
    }

    /**
     * 混合组（#8 mix-blend-mode）捕获上下文：保存混合模式序数与 push..pop 之间已捕获的子命令列表。
     * 由 [pushBlendMode] 创建并压入 [blendGroupStack]，[popBlendMode] 弹出。
     *
     * @property depth 入栈深度（[captureDepthCounter] 自增值），供 [innermostCaptureBuffer] 在两栈交叠时判最内层。
     */
    private class BlendGroupCapture(val mode: Int, val depth: Int) {
        val commands: MutableList<DrawRecord> = mutableListOf()
    }

    /**
     * 历史组（FR-UI-023 historyLayer）捕获上下文：保存 elementId、历史层参数、合成矩形与已捕获的子命令列表。
     * 由 [beginHistoryCapture] 创建并压入 [historyGroupStack]，[endHistoryCapture] 弹出。
     *
     * @property depth 入栈深度（[captureDepthCounter] 自增值），供 [innermostCaptureBuffer] 在三栈交叠时判最内层。
     */
    private data class CaptureBounds(val x: Float, val y: Float, val w: Float, val h: Float)

    private class HistoryGroupCapture(
        val elementId: String,
        val spec: Map<String, *>,
        val bounds: CaptureBounds,
        val depth: Int,
    ) {
        val commands: MutableList<DrawRecord> = mutableListOf()
    }

    private companion object {
        private val log = UiLog("top.wcpe.allincore.ui.runtime.render.layer.LayerBufferingBridge")

        /**
         * 浮层回放时整体抬高的 z（统一取自 [UiRenderZ]）：OVERLAY 与 FLOATING 取不同档位，
         * 高于 ITEM 层 vanilla 物品(150)/数量角标(200)，使 hover/选中/下拉框/弹窗盖住物品图标。
         * 其余层返回 0（不偏移）。
         */
        fun floatingLayerZ(layerId: Int): Float = when (layerId) {
            RenderLayer.OVERLAY.id -> UiRenderZ.OVERLAY
            RenderLayer.FLOATING.id -> UiRenderZ.FLOATING
            else -> 0f
        }

        /**
         * 判断一条记录是否走「立即模式」GPU 绘制（Tesselator 直接 draw 到 framebuffer），
         * 而非 GuiGraphics 的延迟 bufferSource。立即记录回放前需先 flush 已录入的延迟 2D 内容，
         * 保证延迟内容先落盘、立即内容后画在其上（painter 顺序）。
         *
         * 包含 5 类 GPU 特效 + 3 类 styled 批（走 submitBatch 经 GPU shader 立即绘制）；
         * 另含 DRAW_IMAGE / DRAW_TEXTURED_QUAD——二者经 GuiGraphics#blit 走 Tesselator 立即绘制
         * （issue #53），回放前同样需先 flush 延迟内容，否则容器背景 fillRect 可能后刷而盖住图片。
         * DRAW_ITEM_STACK 因平台桥内部已自带 flush，不纳入此集合。
         */
        fun isImmediateRecord(type: RenderCommandType?): Boolean = when (type) {
            RenderCommandType.NEON_GLOW,
            RenderCommandType.FLOW,
            RenderCommandType.ENERGY,
            RenderCommandType.RIPPLE,
            RenderCommandType.PROCEDURAL_TEXTURE,
            RenderCommandType.ROUNDED_RECT,
            RenderCommandType.GRADIENT,
            RenderCommandType.SHADOW,
            RenderCommandType.DRAW_IMAGE,
            RenderCommandType.DRAW_TEXTURED_QUAD,
            // FILTER_GROUP：beginFilterGroup 切到离屏 FBO、endFilterGroup 用 Tesselator 把模糊结果
            // 合成回主屏（立即模式）。回放前需先 flush 已录入的延迟 2D 内容，否则它们会被离屏切换
            // 影响或在 filter 组之后才落盘、错盖在模糊结果之上。
            RenderCommandType.FILTER_GROUP,
            // BACKDROP_FILTER（#8）：就地 blit 主帧缓冲采样背景做高斯（立即模式）。回放前必须先 flush，
            // 保证更早层/更早兄弟的延迟内容已落主帧缓冲，否则采样到空（C1 时序硬约束）。
            RenderCommandType.BACKDROP_FILTER,
            // BLEND_GROUP（#8）：pushBlendMode 切 GL 混合函数、子命令回放、popBlendMode 还原（涉及裸 GL 状态）。
            // 回放前先 flush 已录入的延迟内容，使混合函数只作用于本组子命令、不污染相邻批次。
            RenderCommandType.BLEND_GROUP,
            // MATERIAL_LAYER：走 GPU 材质 shader 立即绘制。回放前先 flush 已录入的延迟内容。
            RenderCommandType.MATERIAL_LAYER,
            // HISTORY_GROUP（FR-UI-023）：beginHistoryCapture 切到离屏隔离靶、endHistoryCapture 捕获元素像素并
            // 合成历史余晖回主屏（立即模式，涉及离屏 FBO 与裸 GL 状态）。回放前先 flush 已录入的延迟内容，
            // 避免它们被离屏切换影响或错盖在合成结果之上（与 FILTER_GROUP 同款时序约束）。
            RenderCommandType.HISTORY_GROUP,
            // PARTICLE（FR-UI-031）：drawParticleLayer 走 GPU 实例化立即绘制。回放前先 flush 已录入的延迟内容，
            // 保证粒子按命令流顺序叠加在正确的元素表面 / 子节点之上（front）或之下（back）。
            RenderCommandType.PARTICLE,
            -> true
            else -> false
        }
    }
}
